<template>
	<div class="db2-kpi" :class="'is-' + variant">
		<div class="db2-kpi-gauge" v-if="variant === 'gauge'">
			<div class="db2-kpi-head">
				<span class="db2-kpi-icon-wrap" :class="accentClass">
					<v-icon class="db2-kpi-icon">{{ icon }}</v-icon>
				</span>
				<div class="db2-kpi-label">
					<v-icon v-if="isEditMode" small class="db2-card-drag" @mousedown.native.stop="onDragStart">mdi-drag</v-icon>
					<span>{{ card.name }}</span>
				</div>
			</div>
			<HaqEChart :option="gaugeOption" height="100%" />
			<div class="db2-kpi-sub is-ok" v-if="sub">{{ sub }}</div>
		</div>
		<template v-else>
			<div class="db2-kpi-row">
				<span class="db2-kpi-icon-wrap" :class="accentClass">
					<v-icon class="db2-kpi-icon">{{ icon }}</v-icon>
				</span>
				<div class="db2-kpi-copy">
					<div class="db2-kpi-label">
						<v-icon v-if="isEditMode" small class="db2-card-drag" @mousedown.native.stop="onDragStart">mdi-drag</v-icon>
						<span>{{ card.name }}</span>
					</div>
					<div class="db2-kpi-value">{{ displayValue }}<span class="db2-kpi-unit" :class="{ 'is-super': unitIsSuper }" v-if="unitLabel">{{ unitLabel }}</span></div>
					<div class="db2-kpi-trend" v-if="trend">
						<span class="db2-kpi-delta" :class="trend.dir">
							<v-icon>{{ trend.dir === 'down' ? 'mdi-triangle-down' : 'mdi-triangle-up' }}</v-icon>
							{{ trend.pct }}
						</span>
						<span class="db2-kpi-vs">{{ trend.rest }}</span>
					</div>
					<div class="db2-kpi-sub" :class="subClass" v-else-if="sub">{{ sub }}</div>
				</div>
			</div>
			<HaqEChart
				class="db2-kpi-spark"
				:class="{ 'is-expandable': !isEditMode }"
				:option="sparkOption"
				height="46px"
				@chart-click="onSparkClick"
			/>
		</template>
		<v-dialog v-model="expandOpen" content-class="haq-dialog db2-kpi-expand" max-width="960px" attach="#app" @click:outside="expandOpen = false">
			<v-card class="haq-dialog-card">
				<v-card-title class="haq-dialog-card-header">
					<h5>{{ card.name }}</h5>
					<v-spacer />
					<v-icon class="close-icon" @click="expandOpen = false">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					<div class="db2-trend">
						<div class="db2-trend-legend">
							<span class="db2-trend-leg">
								<span class="db2-trend-leg-swatch" :style="{ background: sparkColor }"></span>
								{{ expandLegendLabel }}
							</span>
						</div>
						<HaqEChart v-if="expandOpen" class="db2-trend-chart" :option="expandOption" height="100%" />
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import HaqEChart from '../HaqEChart.vue';
import { GRID_GAP, GRID_ROW } from '../dashboardModel';
import { mockPointData, mockAxisLabels } from '../mockPointData';
import { echarts, chartTheme, baseChartOption } from '../chartTheme';

const GREEN = '#4ade80';

/**
 * Comfort-gauge radius in px: previous 85% of the h:2 chart box, minus 6px.
 * ECharts percent radius is min(width, height) / 2; KPI height is the smaller side.
 * @returns {number}
 */
function comfortGaugeRadius() {
	const cardH = GRID_ROW * 2 + GRID_GAP;
	const chartH = cardH - 2 - 12 - 8 - 16;
	return Math.round(0.85 * chartH / 2) - 6;
}

/**
 * Hex or rgb color with an alpha channel for spark fills.
 * @param {string} color
 * @param {number} alpha
 * @returns {string}
 */
function withAlpha(color, alpha) {
	const hex = String(color || '').replace('#', '');
	if (/^[0-9a-fA-F]{6}$/.test(hex)) {
		const n = parseInt(hex, 16);
		return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
	}
	return color || `rgba(91,141,239,${alpha})`;
}

/**
 * Mix a hex color toward white by `amount` (0–1) so the spark fill reads brighter
 * without changing hue.
 * @param {string} color
 * @param {number} amount
 * @returns {string}
 */
function brighten(color, amount) {
	const hex = String(color || '').replace('#', '');
	if (/^[0-9a-fA-F]{6}$/.test(hex)) {
		const n = parseInt(hex, 16);
		const mix = (c) => Math.round(c + (255 - c) * amount);
		const r = mix((n >> 16) & 255);
		const g = mix((n >> 8) & 255);
		const b = mix(n & 255);
		const toHex = (c) => {
			const h = c.toString(16);
			return h.length === 1 ? '0' + h : h;
		};
		return '#' + toHex(r) + toHex(g) + toHex(b);
	}
	return color;
}

/**
 * Y min/max for a spark so the 2px stroke is not clipped at the canvas edge.
 * Count series never scale below 0. Series well above 0 (e.g. temperature) pad
 * around dataMin/dataMax so a fake zero does not squash the spark.
 * @param {number[]} values
 * @param {{ floorZero?: boolean }} [opts]
 * @returns {{ min: number, max: number }}
 */
function sparkYExtent(values, opts) {
	const floorZero = !!(opts && opts.floorZero);
	const nums = (values || []).map(Number).filter((n) => !isNaN(n));
	if (!nums.length) return { min: 0, max: 1 };
	const dataMin = Math.min.apply(null, nums);
	const dataMax = Math.max.apply(null, nums);
	const span = dataMax - dataMin;
	const pad = (span > 0 ? span : (Math.abs(dataMax) || 1)) * 0.12;
	const allNonNeg = dataMin >= 0;
	if (floorZero || (allNonNeg && dataMin === 0)) {
		return { min: 0, max: Math.max(dataMax, 0) + pad };
	}
	let min = dataMin - pad;
	let max = dataMax + pad;
	if (allNonNeg && min < 0) min = 0;
	if (max <= min) max = min + (pad || 1);
	return { min, max };
}

/**
 * Spark tooltip HTML: period label plus value and unit.
 * X is a series index, so the label comes from mockAxisLabels rather than the axis value.
 * @param {Array|object} params
 * @param {string[]} labels
 * @param {string} unit
 * @param {string} [hint]
 * @returns {string}
 */
function formatSparkTooltip(params, labels, unit, hint) {
	const p = Array.isArray(params) ? params[0] : params;
	if (!p) return '';
	const raw = p.value;
	const idx = Array.isArray(raw) ? Math.round(Number(raw[0]) || 0) : (p.dataIndex || 0);
	const val = Array.isArray(raw) ? raw[1] : raw;
	const n = Number(val);
	const valueStr = isNaN(n) ? String(val == null ? '' : val) : String(n);
	const unitStr = unit ? ' ' + unit : '';
	const label = (labels && labels[idx]) || '';
	let html = label ? label + '<br/>' + valueStr + unitStr : valueStr + unitStr;
	if (hint) {
		html += '<br/><span style="opacity:0.7;font-size:12px">' + hint + '</span>';
	}
	return html;
}

/** Axis tick size on the expanded KPI graph (matches TrendCard). */
const EXPAND_AXIS_FONT = 11;

/** Tick-to-axis gap on the expanded KPI graph. */
const EXPAND_AXIS_MARGIN = 8;

/**
 * Compact min/max tick text for the KPI spark Y axis.
 * @param {number} n
 * @returns {string}
 */
function formatSparkAxisNum(n) {
	if (!isFinite(n)) return '';
	const abs = Math.abs(n);
	if (abs >= 100) return String(Math.round(n));
	const r = Math.round(n * 10) / 10;
	if (Math.abs(r - Math.round(r)) < 0.05) return String(Math.round(r));
	return r.toFixed(1);
}

/**
 * Fade-out fill for the expanded KPI graph (same stops as TrendCard).
 * @param {string} color
 * @returns {object}
 */
function expandAreaFill(color) {
	return {
		origin: 'start',
		color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
			{ offset: 0, color: withAlpha(color, 0.31) },
			{ offset: 0.4, color: withAlpha(color, 0.08) },
			{ offset: 1, color: withAlpha(color, 0) }
		])
	};
}

/**
 * KPI with sparkline, gauge, or alarm count.
 */
export default {
	name: 'KpiCard',
	components: { HaqEChart },
	props: {
		card: { type: Object, required: true },
		isEditMode: { type: Boolean, default: false },
		timePeriod: { type: String, default: 'week' },
		refreshKey: { type: Number, default: 0 }
	},
	data() {
		return { expandOpen: false };
	},
	computed: {
		/**
		 * Card variant.
		 * @returns {string}
		 */
		variant() {
			return (this.card.config && this.card.config.variant) || 'sparkline';
		},
		/**
		 * Min/max ticks on the spark Y axis (off unless settings enable it).
		 * @returns {boolean}
		 */
		showAxisLabels() {
			return !!(this.card.config && this.card.config.showAxisLabels) && this.variant !== 'gauge';
		},
		/**
		 * Header icon. Gauge uses a home-comfort glyph; alarm uses the bell.
		 * @returns {string}
		 */
		icon() {
			if (this.variant === 'gauge') return 'mdi-home-thermometer-outline';
			if (this.variant === 'alarm') return 'mdi-bell-ring';
			return (this.card.config && this.card.config.icon) || 'mdi-chart-line';
		},
		/**
		 * Mock series for this card/point.
		 * @returns {object}
		 */
		data() {
			const cfg = this.card.config || {};
			return mockPointData(cfg.pointId || this.card.uniqueId, this.timePeriod, {
				unit: cfg.unit || '',
				base: cfg.base,
				refreshKey: this.refreshKey
			});
		},
		/**
		 * Unit shown next to the value.
		 * @returns {string}
		 */
		unitLabel() {
			return (this.card.config && this.card.config.unit) || this.data.unit || '';
		},
		/**
		 * Degree units sit slightly above the value baseline.
		 * @returns {boolean}
		 */
		unitIsSuper() {
			return String(this.unitLabel).indexOf('°') !== -1;
		},
		/**
		 * Setpoint / status line under the value.
		 * @returns {string}
		 */
		sub() {
			const cfg = this.card.config || {};
			if (cfg.sub) return cfg.sub;
			if (this.variant === 'alarm') return 'Requires attention';
			return '';
		},
		/**
		 * Parsed ↓/↑ percent line from sub, if present.
		 * @returns {{ dir: string, pct: string, rest: string }|null}
		 */
		trend() {
			const m = String(this.sub || '').match(/^([↓↑])\s*([\d.]+%)\s*(.*)$/);
			if (!m) return null;
			return { dir: m[1] === '↓' ? 'down' : 'up', pct: m[2], rest: m[3] };
		},
		/**
		 * Icon / spark accent class.
		 * @returns {string}
		 */
		accentClass() {
			if (this.variant === 'alarm') return 'is-alarm';
			if ((this.icon || '').indexOf('flash') !== -1) return 'is-green';
			return '';
		},
		/**
		 * Line and fill color for the sparkline.
		 * @returns {string}
		 */
		sparkColor() {
			if (this.variant === 'alarm') return chartTheme().red;
			if (this.accentClass === 'is-green') return GREEN;
			return chartTheme().accent;
		},
		/**
		 * Status line color class when not a percent trend.
		 * @returns {string}
		 */
		subClass() {
			const s = this.sub || '';
			if (s.indexOf('Very Good') !== -1) return 'is-ok';
			return '';
		},
		/**
		 * Formatted primary value.
		 * @returns {string}
		 */
		displayValue() {
			if (this.variant === 'alarm') {
				const n = this.card.config && this.card.config.base != null ? this.card.config.base : Math.max(0, Math.round(this.data.value % 12));
				return String(n);
			}
			const raw = this.card.config && this.card.config.base != null ? this.card.config.base : this.data.value;
			if (typeof raw === 'number' && Math.abs(raw) >= 1000000) {
				return (raw / 1000000).toFixed(2) + 'M';
			}
			if (typeof raw === 'number' && Math.abs(raw) >= 1000) return raw.toLocaleString();
			return String(raw);
		},
		/**
		 * Comfort-score ring.
		 * @returns {object}
		 */
		gaugeOption() {
			const t = chartTheme();
			const raw = this.card.config && this.card.config.base != null ? this.card.config.base : this.data.value;
			const val = Math.max(0, Math.min(100, Number(raw) || 0));
			return {
				animation: false,
				series: [{
					type: 'gauge',
					startAngle: 90,
					endAngle: -270,
					radius: comfortGaugeRadius(),
					center: ['50%', '55%'],
					min: 0,
					max: 100,
					pointer: { show: false },
					anchor: { show: false },
					progress: {
						show: true,
						overlap: false,
						roundCap: true,
						clip: false,
						itemStyle: { color: t.accent }
					},
					axisLine: {
						roundCap: true,
						lineStyle: { width: 10, color: [[1, 'rgba(91, 141, 239, 0.16)']] }
					},
					splitLine: { show: false },
					axisTick: { show: false },
					axisLabel: { show: false },
					title: { show: false },
					detail: {
						valueAnimation: false,
						fontSize: 32,
						fontWeight: 400,
						color: '#fff',
						offsetCenter: [0, '4%'],
						formatter: '{value}'
					},
					data: [{ value: Math.round(val) }]
				}]
			};
		},
		/**
		 * Sparkline option with fade-out fill.
		 * Value-axis points sit on the left/right grid edges (icon-aligned). Grid top/bottom
		 * and a padded y scale keep the 2px stroke inside the canvas.
		 * @returns {object}
		 */
		sparkOption() {
			const t = chartTheme();
			const base = baseChartOption(t);
			const color = this.sparkColor;
			const fill = brighten(color, 0.2);
			const raw = this.data.sparkline || [];
			const isCounts = this.variant === 'alarm';
			const values = isCounts ? raw.map((v) => Math.max(0, Number(v) || 0)) : raw;
			const last = Math.max(values.length - 1, 1);
			const y = sparkYExtent(values, { floorZero: isCounts });
			const labels = mockAxisLabels(this.timePeriod, values.length);
			const unit = this.unitLabel;
			const axisOn = this.showAxisLabels;
			const ySpan = (y.max - y.min) || 1;
			return {
				...base,
				grid: {
					left: axisOn ? 38 : 0,
					right: 0,
					top: axisOn ? 10 : 6,
					bottom: axisOn ? 10 : 6,
					width: axisOn ? undefined : '100%',
					containLabel: false
				},
				xAxis: {
					type: 'value',
					min: 0,
					max: last,
					show: false,
					boundaryGap: false,
					axisLine: { show: false },
					axisTick: { show: false },
					axisLabel: { show: false },
					splitLine: { show: false }
				},
				yAxis: {
					type: 'value',
					min: y.min,
					max: y.max,
					interval: axisOn ? ySpan : undefined,
					show: axisOn,
					axisLine: { show: false },
					axisTick: { show: false },
					axisLabel: {
						show: axisOn,
						color: t.muted,
						fontSize: 10,
						fontWeight: 400,
						margin: 4,
						formatter: (val) => formatSparkAxisNum(val)
					},
					splitLine: { show: false }
				},
				series: [{
					type: 'line',
					data: values.map((v, i) => [i, v]),
					showSymbol: false,
					symbol: 'circle',
					symbolSize: 7,
					smooth: true,
					clip: false,
					lineStyle: { width: 2, color },
					itemStyle: { color: color, borderColor: '#fff', borderWidth: 2 },
					areaStyle: {
						origin: 'start',
						color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
							{ offset: 0, color: withAlpha(fill, 0.42) },
							{ offset: 1, color: withAlpha(fill, 0) }
						])
					}
				}],
				// Overlay on the card; parent overflow:hidden would clip a 38px chart tooltip.
				tooltip: {
					...base.tooltip,
					confine: true,
					appendToBody: true,
					axisPointer: {
						type: 'line',
						snap: true,
						lineStyle: { color: t.muted, width: 1, type: 'dotted' }
					},
					formatter: (params) => formatSparkTooltip(params, labels, unit, this.isEditMode ? '' : 'Click to expand')
				}
			};
		},
		/**
		 * Legend caption with unit, matching the trend card.
		 * @returns {string}
		 */
		expandLegendLabel() {
			const name = this.card.name || 'Value';
			const unit = this.unitLabel;
			if (!unit) return name;
			if (String(name).indexOf('(') >= 0) return name;
			return name + ' (' + unit + ')';
		},
		/**
		 * Full graph option styled like Zone Performance Trends.
		 * @returns {object}
		 */
		expandOption() {
			const t = chartTheme();
			const color = this.sparkColor;
			const raw = this.data.sparkline || [];
			const isCounts = this.variant === 'alarm';
			const values = isCounts ? raw.map((v) => Math.max(0, Number(v) || 0)) : raw;
			const y = sparkYExtent(values, { floorZero: isCounts });
			const labels = mockAxisLabels(this.timePeriod, values.length);
			const unit = this.unitLabel;
			const labelStyle = { fontSize: EXPAND_AXIS_FONT, fontWeight: 400 };
			return {
				...baseChartOption(t),
				textStyle: { fontSize: EXPAND_AXIS_FONT },
				legend: { show: false },
				grid: { left: 58, right: 24, top: 30, bottom: 34, containLabel: false },
				xAxis: {
					type: 'category',
					data: labels,
					boundaryGap: false,
					axisTick: { show: false },
					axisLine: { show: false },
					splitLine: { show: false },
					textStyle: { fontSize: EXPAND_AXIS_FONT },
					nameTextStyle: { fontSize: EXPAND_AXIS_FONT },
					axisLabel: { color: t.muted, ...labelStyle, fontSize: EXPAND_AXIS_FONT }
				},
				yAxis: {
					type: 'value',
					position: 'left',
					min: y.min,
					max: y.max,
					name: unit || '',
					nameLocation: 'end',
					nameGap: 8,
					nameRotate: 0,
					textStyle: { fontSize: EXPAND_AXIS_FONT },
					nameTextStyle: {
						color: color,
						...labelStyle,
						fontSize: EXPAND_AXIS_FONT,
						align: 'right',
						padding: [0, EXPAND_AXIS_MARGIN, 0, 0]
					},
					splitLine: { show: true, lineStyle: { color: t.line } },
					axisTick: { show: false },
					axisLine: { show: false },
					axisLabel: {
						color: color,
						...labelStyle,
						fontSize: EXPAND_AXIS_FONT,
						align: 'right',
						margin: EXPAND_AXIS_MARGIN
					}
				},
				series: [{
					name: this.card.name || 'Value',
					type: 'line',
					smooth: true,
					showSymbol: false,
					data: values,
					itemStyle: { color: color },
					lineStyle: { width: 2, color: color },
					areaStyle: expandAreaFill(color)
				}],
				tooltip: {
					...baseChartOption(t).tooltip,
					axisPointer: {
						type: 'line',
						snap: true,
						lineStyle: { color: t.muted, width: 1, type: 'dotted' }
					},
					formatter: (params) => formatSparkTooltip(params, labels, unit)
				}
			};
		}
	},
	methods: {
		/**
		 * Open the full graph when the sparkline is clicked (view mode only).
		 */
		onSparkClick() {
			if (this.isEditMode || this.variant === 'gauge') return;
			this.expandOpen = true;
		},
		/**
		 * Start a canvas move from the in-card title handle.
		 * @param {MouseEvent} e
		 */
		onDragStart(e) {
			if (!this.isEditMode) return;
			this.$emit('drag-start', e);
		}
	}
};
</script>
